import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/i18n";

export default function ProcessSection() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 70%", "end 30%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="process"
      data-testid="process-section"
      ref={ref}
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-10 h-px bg-lux" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
            {t.process.label}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl text-balance mb-20"
        >
          {t.process.title}
        </motion.h2>

        <div className="relative">
          {/* Timeline rail */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-white/8" />
          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-white via-white/60 to-transparent origin-top"
          />

          <div className="space-y-16 md:space-y-20">
            {t.process.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                data-testid={`process-step-${i}`}
                className="relative pl-16 md:pl-24"
              >
                <div className="absolute left-0 top-2 flex items-center justify-center w-12 md:w-16">
                  <span className="w-3 h-3 rounded-full bg-lux relative z-10">
                    <span className="absolute inset-0 rounded-full bg-lux animate-soft-pulse" />
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-10 mb-3">
                  <span className="font-display text-sm text-lux tracking-[0.25em]">
                    {s.n}
                  </span>
                  <h3 className="font-display text-3xl md:text-5xl font-light tracking-tighter">
                    {s.t}
                  </h3>
                </div>
                <p className="text-white/60 max-w-xl md:pl-[5.5rem]">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
